import { Group, Text } from '@mantine/core';
import { IconArrowUpRight, IconFolder, IconLock } from '@tabler/icons-react';
import type { Folder } from '../../../schemas/folder.schema';
import { AccessIcon } from './FolderTree';
import { FileTypeIcon } from './FileTypeIcon';
import { isFolderRow, isShortcutRow, type DriveRow } from './driveRow';
import { folderIconColor, getEffectiveAccess, isDriveAccess } from './driveAccess';
import { getDriveFolderDecoration } from '../../registry/defineDriveDecorations';
import { DriveDecorationMark } from './DriveDecorationMark';

export interface DriveNameCellProps {
  /** Строка среза Диска: файл, папка или ярлык. */
  row: DriveRow;
  /**
   * Папки Диска по id — нужны для цвета иконки папки (эффективный режим зоны
   * считается подъёмом по access_root_id). Без них папка рисуется нейтрально.
   */
  foldersById?: Map<number, Folder>;
  /**
   * Путь к папке записи («Проекты / Проект А»). Показывается второй строкой —
   * так в результатах глобального поиска видно, откуда документ. Пусто — не
   * показывается.
   */
  location?: string;
}

/**
 * DriveNameCell — ячейка «Имя» строки Диска: иконка типа + имя, у папки —
 * значок режима доступа, у ярлыка — стрелка-пометка и приглушённый текст.
 *
 * Единая для всех списков Диска (страница, файл-пикер, поиск, встраиваемая
 * панель): раньше эта разметка была скопирована в каждый из них и расходилась.
 */
export function DriveNameCell({ row, foldersById, location }: DriveNameCellProps) {
  const name = row.original_name ?? row.filename;

  const nameNode = (() => {
    if (isFolderRow(row)) {
      const folder = foldersById?.get(row.id);
      const effective = folder && foldersById ? getEffectiveAccess(folder, foldersById) : null;
      // Доменный признак папки (defineDriveDecorations) — бейдж после имени.
      // Акцентную полоску, как в дереве, строка таблицы не рисует: у неё нет
      // собственной рамки, цвет несёт сам бейдж
      const decoration = getDriveFolderDecoration(folder);
      return (
        <Group gap={8} wrap='nowrap'>
          <IconFolder size={16} color={folderIconColor(effective)} style={{ flexShrink: 0 }} />
          <Text size='sm' fw={500} truncate>
            {name}
          </Text>
          <DriveDecorationMark decoration={decoration} />
          {isDriveAccess(row.entry_access) && <AccessIcon access={row.entry_access} />}
          {/* Системная папка — каркас раскладки, юзер её не переименовывает */}
          {row.entry_is_system === true && (
            <IconLock size={12} color='var(--mantine-color-gray-5)' />
          )}
        </Group>
      );
    }

    if (isShortcutRow(row)) {
      return (
        <Group gap={8} wrap='nowrap'>
          <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
            <FileTypeIcon mimeType={row.mime_type} name={name} />
            <IconArrowUpRight
              size={11}
              stroke={3}
              color='var(--mantine-color-blue-6)'
              style={{
                position: 'absolute',
                left: -4,
                bottom: -4,
                background: 'var(--mantine-color-body)',
                borderRadius: 3,
              }}
            />
          </span>
          <Text size='sm' c='dimmed' truncate>
            {name}
          </Text>
          <Text size='xs' c='dimmed' style={{ flexShrink: 0 }}>
            ярлык
          </Text>
        </Group>
      );
    }

    return (
      <Group gap={8} wrap='nowrap'>
        <FileTypeIcon mimeType={row.mime_type} name={name} />
        <Text size='sm' truncate>
          {name}
        </Text>
      </Group>
    );
  })();

  if (!location) return nameNode;

  return (
    <div style={{ minWidth: 0 }}>
      {nameNode}
      <Text size='xs' c='dimmed' truncate title={location}>
        {location}
      </Text>
    </div>
  );
}
